import { graphql, useFragment as unmask, type FragmentType } from '~/graphql';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import type {
  PaginatedFetchOptions,
  RouteSort,
} from '~/hooks/usePaginatedQuery';
import { BUY_LISTS_QUERY_KEY } from '~/api/buyList';
import { useOrganizationsContactData } from '~/api/organizationContactData';
import type { ContactDetails, ContactsStatus } from '~/utils/contact';

export const primaryContactFragment = graphql(`
  fragment PrimaryContact on User {
    id
    firstName
    lastName
  }
`);

type PrimaryContactRef = FragmentType<typeof primaryContactFragment>;

export function unmaskPrimaryContact(
  ref: PrimaryContactRef | null | undefined
) {
  return unmask(primaryContactFragment, ref);
}

export const SELLER_CONTACTS_PAGE_QUERY_KEY = 'sellerContactsPage';

const sellerContactsPageQuery = graphql(`
  query SellerContactsPage(
    $organizationId: ID!
    $page: Int!
    $size: Int!
    $excludeContactIds: [ID!]
    $onlyContactIds: [ID!]
  ) {
    sellerContactsPage(
      organizationId: $organizationId
      pageInput: { page: $page, size: $size }
      excludeContactIds: $excludeContactIds
      onlyContactIds: $onlyContactIds
    ) {
      content {
        id
        firstName
        lastName
        phoneNumber
        externalId
      }
      paging {
        totalElements
        totalPages
        number
        size
      }
    }
  }
`);

export type SellerContactsPageFilters = {
  organizationId: string;
  excludeContactIds?: string[];
  onlyContactIds?: string[];
};

export const useGetSellerContactsPage = (
  page: number,
  pageSize: number,
  filters?: SellerContactsPageFilters,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const organizationId = filters ? filters.organizationId : '';
  const excludeContactIds = filters ? filters.excludeContactIds : undefined;
  const onlyContactIds = filters ? filters.onlyContactIds : undefined;
  return useGraphqlQuery(
    sellerContactsPageQuery,
    [
      SELLER_CONTACTS_PAGE_QUERY_KEY,
      organizationId,
      page,
      pageSize,
      JSON.stringify(excludeContactIds ?? null),
      JSON.stringify(onlyContactIds ?? null),
    ],
    { organizationId, page, size: pageSize, excludeContactIds, onlyContactIds },
    {
      placeholderData: options?.placeholderData,
      enabled: (options?.enabled ?? true) && organizationId !== '',
    }
  );
};

const chooseQuoteContactsMutation = graphql(`
  mutation ChooseQuoteContacts(
    $quoteId: ID!
    $primaryContactUserId: ID!
    $recipientUserIds: [ID!]!
  ) {
    chooseQuoteContacts(
      quoteId: $quoteId
      primaryContactUserId: $primaryContactUserId
      recipientUserIds: $recipientUserIds
    ) {
      id
    }
  }
`);

export const useChooseQuoteContacts = () =>
  useGraphqlMutation(
    chooseQuoteContactsMutation,
    ['requestForQuote', BUY_LISTS_QUERY_KEY],
    undefined,
    { invalidateOnError: true }
  );

export function useSellerContacts(organizationId: string): {
  contacts: ContactDetails[];
  status: ContactsStatus;
} {
  const { data, isLoading, error } = useOrganizationsContactData([
    organizationId,
  ]);
  const contacts: ContactDetails[] =
    data?.organizationsByIds[0]?.sellerContacts ?? [];
  if (isLoading) {
    return { contacts, status: 'loading' };
  }
  if (error && !data) {
    return { contacts, status: 'failed' };
  }
  return { contacts, status: 'loaded' };
}
